import posthog from 'posthog-js';

let initialized = false;

/**
 * Sem `VITE_POSTHOG_KEY` o analytics inteiro vira no-op silencioso — dev e
 * preview rodam sem key e não podem quebrar nem poluir o console.
 */
export function initAnalytics(): void {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key || initialized) return;

  posthog.init(key, {
    // `||` e não `??`: uma env declarada vazia (`VITE_POSTHOG_HOST=`) passa
    // pelo `??` e inicializaria o posthog com api_host vazio.
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com',

    // `'history_change'` EXPLÍCITO, e não o default.
    //
    // O default do posthog-js 1.309 resolve para `true` quando `defaults` não é
    // passado — e `true` é o comportamento LEGADO: captura `$pageview` só na
    // carga da página. Isto aqui é uma SPA de React Router: com `true`, ir de
    // `/` para `/marca/farm` e daí para `/produto/x` não gera pageview nenhum,
    // e o número mediria recargas de aba em vez de navegação.
    //
    // Omitir a opção portanto não é "ligar o automático" — é ligar a versão
    // dela que quase não registra nada num app como este.
    //
    // Convive com os eventos explícitos (`catalog_viewed`, `kit_viewed`) em vez
    // de duplicá-los: nomes diferentes, perguntas diferentes. O `$pageview` dá
    // a linha de base de tráfego, inclusive nas telas que ninguém instrumentou;
    // os explícitos carregam o payload de domínio.
    capture_pageview: 'history_change',
    defaults: '2025-11-30',
  });
  initialized = true;
}

/** `false` sem `VITE_POSTHOG_KEY` — quem lê o PostHog direto (flags) checa aqui. */
export function isAnalyticsReady(): boolean {
  return initialized;
}

export function track(event: string, props?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(event, props);
}

/**
 * Para um evento disparado logo antes de a página morrer (sair da conta,
 * fechar a aba). O `track` normal entra no lote e o lote sai a cada poucos
 * segundos — a navegação chega antes e o evento some. Aqui ele vai na hora,
 * por `sendBeacon`, que o navegador entrega mesmo depois de descarregar a
 * página.
 */
export function trackBeforeLeave(
  event: string,
  props?: Record<string, unknown>
): void {
  if (!initialized) return;
  posthog.capture(event, props, {
    send_instantly: true,
    transport: 'sendBeacon',
  });
}

/**
 * Liga os eventos à conta, com o id OPACO e mais nada.
 *
 * Toda tela instrumentada é área logada, então não identificar não protege
 * ninguém — só quebra o funil: o mesmo buyer no celular e no desktop vira duas
 * pessoas. O que protege é o QUE se manda.
 *
 * **Nunca envie PII.** O `AuthUser` tem `email`, `phone` e `name`; nenhum dos
 * três entra aqui. `role` entra porque não é dado pessoal e resolve um
 * problema real: sem ele, toda navegação de admin e employee polui o funil com
 * tráfego interno, indistinguível do cliente.
 */
export function identifyUser(id: string, role: string | null): void {
  if (!initialized || !id) return;
  posthog.identify(id, { role: role ?? 'sem_papel' });
}

/**
 * Desfaz a identificação. **Obrigatório no logout.**
 *
 * Sem isto o próximo login no mesmo dispositivo herda o `distinct_id` do
 * anterior, e o PostHog funde as duas pessoas num funil só — dois clientes
 * compartilhando computador viram um. É defeito de dado, não refinamento, e
 * ele não aparece em lugar nenhum até alguém questionar um número.
 */
export function resetIdentity(): void {
  if (!initialized) return;
  posthog.reset();
}
